import {useCallback, useState} from 'react';
import {utmFormat} from 'common/util/Util.tsx';

export const HTTPS = 'https://';
const ANY_URL_SCHEME_REGEXP = /^[a-z][a-z\d+.-]*:/i;
const URL_SCHEME_REGEXP = /^https?:\/\//i;
const URL_SCHEME_TYPING_PREFIX_REGEXP = /^h(?:t(?:t(?:p(?:s?)?)?)?)?$/i;
const PARTIAL_URL_SCHEME_REGEXP = /^https?:?\/{0,2}$/i;
const DUPLICATED_PARTIAL_URL_SCHEME_REGEXP = /^https?:\/\/(https?:?\/{0,2})$/i;
const DUPLICATED_URL_SCHEME_REGEXP = /^(https?:\/\/)(https?:\/\/)+/i;
const URL_LIKE_WITHOUT_SCHEME_REGEXP = /^localhost(?::\d+)?(?:[/?#].*)?$|^[^\s/?#]+\.[^\s]+/i;

export const formatLandingPageUrlInput = (raw: string, isReplacingValue = false) => {
    const value = raw.trim();

    if (!isReplacingValue || !value) return raw;
    if (ANY_URL_SCHEME_REGEXP.test(value) || URL_SCHEME_TYPING_PREFIX_REGEXP.test(value)) return raw;
    return HTTPS + value;
};

export const hasUtmTags = (url: string) => {
    try {
        const urlWithScheme = URL_SCHEME_REGEXP.test(url) ? url : HTTPS + url;
        const params = new URLSearchParams(new URL(urlWithScheme).search);
        return params.has('utm_source') && params.has('utm_medium') && params.has('utm_campaign') && params.has('utm_id');
    } catch {
        return false;
    }
};

export const handleUtmTags = (isChecked: boolean, url: string, format: any) => {
    let result = url;
    if (isChecked) {
        if (!result.includes('utm_source=')) result += `${result.includes('?') ? '&' : '?'}utm_source=adsp`;
        if (!result.includes('utm_medium=')) result += `&utm_medium=${utmFormat[format]}`;
        if (!result.includes('utm_ad_group=')) result += `&utm_ad_group={AD_GROUP}`;
        if (!result.includes('utm_id=')) result += `&utm_id={BANNER}`;
    } else {
        result = result
            .replace(/([?&])utm_source=[^&]*(&|$)/, '$1')
            .replace(/([?&])utm_medium=[^&]*(&|$)/, '$1')
            .replace(/([?&])utm_ad_group=\{AD_GROUP}(&|$)/, '$1')
            .replace(/([?&])utm_id=\{BANNER}(&|$)/, '$1')
            .replace(/&$/, '').replace(/\?$/, '');
    }
    return result;
};

export function updateUtmFormat(url: string, value: any, setLandingPage: (value: string) => void) {
    if (url && url.includes('utm_medium=')) {
        const cleaned = url.replace(/([?&])utm_medium=[^&]*(&|$)/, '$1').replace(/&$/, '');
        setLandingPage(cleaned + `&utm_medium=${utmFormat[value]}`);
    }
}

export const normalizeLandingPageUrl = (raw: string) => {
    let value = raw.trim();

    if (!value) return '';
    if (URL_SCHEME_TYPING_PREFIX_REGEXP.test(value)) return value;
    if (PARTIAL_URL_SCHEME_REGEXP.test(value)) return value;
    const duplicatedPartialScheme = value.match(DUPLICATED_PARTIAL_URL_SCHEME_REGEXP);
    if (duplicatedPartialScheme) return duplicatedPartialScheme[1];
    if (!ANY_URL_SCHEME_REGEXP.test(value) && URL_LIKE_WITHOUT_SCHEME_REGEXP.test(value)) value = HTTPS + value;

    return value.replace(DUPLICATED_URL_SCHEME_REGEXP, '$1');
};

export function useUtm(initialUrl: string) {
    const [url, setUrl] = useState(initialUrl);
    const [withTags, setWithTags] = useState(hasUtmTags(initialUrl));
    const applyFormat = useCallback((fmt: any) => {
        updateUtmFormat(url, fmt, setUrl);
    }, [url]);

    const toggleTags = useCallback((checked: boolean, fmt: any) => {
        setWithTags(checked);
        setUrl(prev => handleUtmTags(checked, prev, fmt));
    }, []);

    const changeRaw = useCallback((raw: string) => {
        setUrl(raw);
        setWithTags(hasUtmTags(raw));
    }, []);

    return {url, setUrl, withTags, toggleTags, changeRaw, applyFormat};
}
